/**
 * Générateur — Automatisme : préfixes et unités de longueur
 * Attendu ID : 1020 | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève connaît les
 * significations des préfixes allant du kilo- au milli-, ainsi que les
 * relations entre le mètre, ses multiples et ses sous-multiples, et fait le
 * lien avec les unités de numération du système décimal. »
 * Labels (cartes exerciseur) :
 *   - Préfixes et unités de longueur
 *
 * ── Plan de cartes (2026-08-21, validé prof) ───────────────────────────────
 * DOMAINE FERMÉ, rien de plus rien de moins que l'automatisme :
 *   - les 6 SIGNIFICATIONS de préfixes (kilo → mille ... milli → un
 *     millième), en MOTS SEULS (arbitrage prof) ;
 *   - les 12 RELATIONS à cheval sur le mètre, DEUX directions (arbitrage
 *     prof : « 1 cm = 0,01 m » ET la phrase modèle du programme
 *     « 1 m = 100 cm ») — réponses toujours dans {10 ; 100 ; 1 000} ou
 *     {0,1 ; 0,01 ; 0,001} ;
 *   - le lien numération (classer : 1 dm = 1/10 m = 0,1 m, la boucle avec
 *     les gens 1005/1006).
 * INTERDITS : aucune conversion de valeur quelconque (pas de 2,4 km),
 * aucun tableau de conversion (le programme l'écarte explicitement), pas
 * de paires d'unités hors mètre (dm ↔ cm = l'attendu suivant).
 * Tout fait émis est recalculé sur la table des exposants (throw sinon) ;
 * aucune arithmétique flottante (exposants entiers uniquement).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeRelier, makeClasser,
} from '../../../utils';

const ATTENDU_ID = 1020;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Préfixes et unités de longueur';

// ─── La table des exposants (source de vérité unique) ─────────────────────

const UNITES = [
    { sym: 'km', prefixe: 'kilo-', exp: 3 },
    { sym: 'hm', prefixe: 'hecto-', exp: 2 },
    { sym: 'dam', prefixe: 'déca-', exp: 1 },
    { sym: 'dm', prefixe: 'déci-', exp: -1 },
    { sym: 'cm', prefixe: 'centi-', exp: -2 },
    { sym: 'mm', prefixe: 'milli-', exp: -3 },
];

/** Signification des préfixes, en mots seuls (arbitrage prof 2026-08-21). */
const SIGNIFICATIONS = {
    3: 'mille', 2: 'cent', 1: 'dix',
    '-1': 'un dixième', '-2': 'un centième', '-3': 'un millième',
};

/** 10^exp en écriture française (jamais de flottant : table fermée). */
const VALEURS = {
    3: '1 000', 2: '100', 1: '10',
    '-1': '0,1', '-2': '0,01', '-3': '0,001',
};

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1020 : ${message}`);
};

/**
 * Un FAIT-relation : « 1 <gauche> = <valeur> <droite> ». sens 'vers_m' :
 * 1 km = 1 000 m (valeur = 10^exp) ; sens 'depuis_m' : 1 m = 100 cm
 * (valeur = 10^(−exp)). La valeur sort de la table, jamais d'un calcul.
 */
const fait = (u, sens) => {
    const e = sens === 'vers_m' ? u.exp : -u.exp;
    garde(VALEURS[e] !== undefined, `exposant ${e} hors table`);
    return {
        gauche: sens === 'vers_m' ? u.sym : 'm',
        droite: sens === 'vers_m' ? 'm' : u.sym,
        valeur: VALEURS[e],
        exp: e,
        cle: `${u.sym}|${sens}`,
    };
};

const tirerFaits = (nb) => {
    const tous = [];
    for (const u of UNITES) {
        tous.push(fait(u, 'vers_m'));
        tous.push(fait(u, 'depuis_m'));
    }
    // tiroirs : au moins un fait à réponse entière ET un à réponse décimale
    for (let essai = 0; essai < 30; essai++) {
        const tirage = shuffle(tous).slice(0, nb);
        const entiers = tirage.filter((f) => !f.valeur.includes(','));
        if (entiers.length >= 1 && entiers.length < tirage.length) return tirage;
    }
    return shuffle(tous).slice(0, nb);
};

// ─── Format 1 : relier préfixe → signification ────────────────────────────

const genPrefixesRelier = () => {
    // 3 ou 4 paires parmi 6 (C(6,3) + C(6,4) = 35 combinaisons) et 1 ou 2
    // leurres : la variété vient du sous-ensemble, le domaine reste fermé
    const nb = randomChoice([3, 4]);
    const unites = shuffle([...UNITES]).slice(0, nb);
    const pairs = unites.map((u) => ({ source: u.prefixe, target: SIGNIFICATIONS[u.exp] }));
    // leurres = significations des préfixes NON tirés (domaine fermé : jamais
    // d'invention), chacun piège la confusion avec un préfixe voisin
    const leurres = shuffle(UNITES.filter((u) => !unites.includes(u)))
        .slice(0, randomChoice([1, 2]))
        .map((u) => ({ target: SIGNIFICATIONS[u.exp], code: 'signification_dun_autre_prefixe' }));
    const payload = makeRelier(
        pairs,
        '<p><b>Relie chaque préfixe à sa signification.</b></p>',
        { ...opts(L1), cibles_leurres: leurres },
    );
    payload._meta.params = { prefixes: unites.map((u) => u.prefixe), leurres };
    return payload;
};

// ─── Format 2 : relations à trous (saisie) ────────────────────────────────

const genRelationsTrouSaisie = () => {
    const faits = tirerFaits(4);
    // une égalité par ligne (retour prof 2026-08-21 : le point-virgule tassait tout)
    const phrase = faits.map((f) => `1 ${f.gauche} = {{${f.valeur}}} ${f.droite}`).join('\n');
    return makeTexteTrou(
        phrase,
        '<p><b>Complète les égalités.</b></p>',
        { ...opts(L1), mode: 'saisie' },
    );
};

// ─── Format 3 : QCM à erreurs nommées ─────────────────────────────────────

const genRelationsQcm = () => {
    for (let essai = 0; essai < 40; essai++) {
        const u = randomChoice(UNITES);
        const sens = randomChoice(['vers_m', 'depuis_m']);
        const f = fait(u, sens);
        const candidats = [];
        // un rang de trop / de moins (quand l'exposant décalé reste en table)
        for (const dec of shuffle([1, -1])) {
            if (VALEURS[f.exp + dec] !== undefined) {
                candidats.push({ valeur: VALEURS[f.exp + dec], code: 'rang_decale' });
            }
        }
        // la relation lue À L'ENVERS (1 dm = 10 m : récitation inversée)
        candidats.push({ valeur: VALEURS[-f.exp], code: 'relation_inversee' });
        // en complément : la valeur d'un autre préfixe du domaine
        for (const autre of shuffle([...UNITES])) {
            candidats.push({ valeur: VALEURS[autre.exp], code: 'autre_prefixe' });
        }
        const vus = new Set([f.valeur]);
        const dis = candidats.filter((c) => {
            if (vus.has(c.valeur)) return false;
            vus.add(c.valeur);
            return true;
        }).slice(0, 3);
        if (dis.length < 3) continue;
        const payload = makeQcm(
            [f.valeur, ...dis.map((c) => c.valeur)],
            f.valeur,
            `<p><b>Complète : 1 ${f.gauche} = ... ${f.droite}</b></p>`,
            opts(L1),
            false,
        );
        payload._meta.params = { fait: f.cle, distracteurs: dis };
        return payload;
    }
    const payload = makeQcm(['1 000', '100', '0,001', '10'], '1 000',
        '<p><b>Complète : 1 km = ... m</b></p>', opts(L1), false);
    payload._meta.params = { fait: 'km|vers_m', distracteurs: [] };
    return payload;
};

// ─── Format 4 : tableau Vrai/Faux ─────────────────────────────────────────

const genRelationsVraiFaux = () => {
    for (let essai = 0; essai < 40; essai++) {
        const faits = tirerFaits(4);
        const lignes = [];
        const verites = [];
        for (const f of faits) {
            let valeurAffichee;
            if (Math.random() < 0.5) valeurAffichee = f.valeur;
            else {
                // erreurs NOMMÉES : rang décalé ou relation inversée
                const fausses = [];
                if (VALEURS[f.exp + 1] !== undefined) fausses.push(VALEURS[f.exp + 1]);
                if (VALEURS[f.exp - 1] !== undefined) fausses.push(VALEURS[f.exp - 1]);
                if (VALEURS[-f.exp] !== f.valeur) fausses.push(VALEURS[-f.exp]);
                valeurAffichee = randomChoice(fausses);
            }
            // Vérité RE-CALCULÉE sur la table (jamais l'intention) :
            const verite = valeurAffichee === VALEURS[f.exp];
            lignes.push(`1 ${f.gauche} = ${valeurAffichee} ${f.droite}`);
            verites.push(verite);
        }
        const nbVraies = verites.filter(Boolean).length;
        if (nbVraies < 1 || nbVraies > 3) continue;
        return makeVraiFaux(
            lignes,
            verites.map((v) => (v ? 0 : 1)),
            '<p><b>Vrai ou faux ?</b></p>',
            opts(L1),
        );
    }
    return makeVraiFaux(
        ['1 km = 1 000 m', '1 cm = 0,1 m', '1 m = 100 cm', '1 dam = 0,1 m'],
        [0, 1, 0, 1],
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Format 5 : classer (le lien avec la numération) ──────────────────────

const genNumerationClasser = () => {
    // catégories = sous-multiples en mètre ; items = l'unité, la fraction,
    // et l'écriture « préfixe » : la boucle 1 dm = 1/10 m = 0,1 m
    const SOUS_MULTIPLES = [
        { exp: -1, cat: '= 0,1 m', items: ['1 dm', '$\\dfrac{1}{10}$ m', 'un dixième de mètre'] },
        { exp: -2, cat: '= 0,01 m', items: ['1 cm', '$\\dfrac{1}{100}$ m', 'un centième de mètre'] },
        { exp: -3, cat: '= 0,001 m', items: ['1 mm', '$\\dfrac{1}{1\\,000}$ m', 'un millième de mètre'] },
    ];
    const groupes = shuffle([...SOUS_MULTIPLES]).slice(0, randInt(2, 3));
    const categories = groupes.map((g) => ({
        name: g.cat,
        items: shuffle([...g.items]).slice(0, 2),
    }));
    const tous = categories.flatMap((c) => c.items);
    garde(new Set(tous).size === tous.length, 'classer : item dupliqué');
    return makeClasser(
        categories,
        '<p><b>Range chaque écriture dans la bonne colonne.</b></p>',
        opts(L1),
    );
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'domaine FERMÉ : 6 préfixes + 12 relations à cheval sur le mètre, valeurs de la table {1 000, 100, 10, 0,1, 0,01, 0,001} uniquement',
        'jamais de conversion de valeur quelconque, jamais de tableau de conversion, jamais de paire hors mètre (dm ↔ cm = attendu suivant)',
    ],
    relations_qcm_unique: {
        support: 'une relation « 1 <unité> = ... <unité> », les deux directions',
        fixes: {
            rang_decale: 'un rang de trop ou de moins (100 au lieu de 1 000)',
            relation_inversee: 'la relation récitée à l\'envers (1 dm = 10 m)',
            autre_prefixe: 'la valeur d\'un autre préfixe du domaine',
        },
    },
    prefixes_relier: {
        support: '4 préfixes parmi 6, cibles = significations en mots seuls',
        fixes: { signification_dun_autre_prefixe: 'la signification d\'un préfixe non tiré (domaine fermé)' },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    prefixes_relier: genPrefixesRelier,
    relations_texte_trou_saisie: genRelationsTrouSaisie,
    relations_qcm_unique: genRelationsQcm,
    relations_tableau_vf: genRelationsVraiFaux,
    numeration_classer: genNumerationClasser,
};

const FORMAT_LABELS = {
    prefixes_relier: 'Relier',
    relations_texte_trou_saisie: 'Saisie',
    relations_qcm_unique: 'QCM',
    relations_tableau_vf: 'Vrai/Faux',
    numeration_classer: 'Classer',
};

export const CATEGORIES = [
    {
        id: 'prefixes',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
